import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'

interface MCPToolCall {
  id: number
  created_at: string
  agent_id: number | null
  agent_name: string | null
  connection_id: number
  connection_name: string | null
  server_name: string | null
  tool_name: string
  tool_arguments: any
  success: boolean
  result_text: string | null
  error_message: string | null
  duration_ms: number
}

interface MCPLogsResponse {
  items: MCPToolCall[]
  total: number
  page: number
  per_page: number
  pages: number
}

export default function MCPLogsPage() {
  const [logs, setLogs] = useState<MCPToolCall[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  
  // Filters
  const [filterSuccess, setFilterSuccess] = useState<string>('all')
  const [filterToolName, setFilterToolName] = useState('')
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const fetchLogs = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params: any = {
        page,
        per_page: perPage,
      }
      
      if (filterSuccess !== 'all') {
        params.success = filterSuccess === 'success'
      }
      
      if (filterToolName.trim()) {
        params.tool_name = filterToolName.trim()
      }
      
      const response = await api.get<MCPLogsResponse>('/mcp/tool-calls', { params })
      setLogs(response.data.items)
      setTotal(response.data.total)
    } catch (err: any) {
      console.error('Error fetching MCP logs:', err)
      setError(err.response?.data?.detail || 'Erreur lors du chargement des logs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLogs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterSuccess, filterToolName])

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return
    
    const interval = setInterval(() => {
      fetchLogs()
    }, 10000)
    
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, page, filterSuccess, filterToolName])

  const toggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    return date.toLocaleString('fr-FR')
  }

  const formatDuration = (ms: number | null) => {
    if (ms === null || ms === undefined) return '-'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }

  const totalPages = Math.ceil(total / perPage)

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-6 px-6 py-6">
        <div className="mb-4">
          <button
            onClick={() => window.location.href = '/admin'}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour
          </button>
        </div>
        <section className="space-y-6 rounded-3xl border border-emerald-100/70 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          <div className="flex items-center justify-end gap-2">
          <select
            value={autoRefresh ? 'on' : 'off'}
            onChange={(e) => setAutoRefresh(e.target.value === 'on')}
            className="px-3 py-1.5 rounded border bg-transparent"
          >
            <option value="on">Auto-refresh (10s)</option>
            <option value="off">Manuel</option>
          </select>
          <button
            onClick={fetchLogs}
            className="px-3 py-1.5 rounded border hover:bg-zinc-50 dark:hover:bg-zinc-800"
            disabled={loading}
          >
            {loading ? 'Chargement…' : 'Rafraîchir'}
          </button>
          </div>

          {/* Filters */}
          <div className="p-4 rounded border border-zinc-200 dark:border-zinc-800 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm mb-1">Nom de l'outil</label>
            <input
              type="text"
              value={filterToolName}
              onChange={(e) => {
                setFilterToolName(e.target.value)
                setPage(1)
              }}
              placeholder="Ex: get_marine_weather"
              className="w-full px-3 py-1.5 rounded border bg-transparent"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Statut</label>
            <select
              value={filterSuccess}
              onChange={(e) => {
                setFilterSuccess(e.target.value)
                setPage(1)
              }}
              className="w-full px-3 py-1.5 rounded border bg-transparent"
            >
              <option value="all">Tous</option>
              <option value="success">Succès uniquement</option>
              <option value="error">Erreurs uniquement</option>
            </select>
          </div>
        </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-200">
              {error}
            </div>
          )}

          <div className="overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left border-b border-zinc-200 dark:border-zinc-800">
            <tr>
              <th className="py-2 pr-2 w-8"></th>
              <th className="py-2 pr-4">Date</th>
              <th className="py-2 pr-4">Agent</th>
              <th className="py-2 pr-4">Outil</th>
              <th className="py-2 pr-4">Serveur</th>
              <th className="py-2 pr-4 text-center">Statut</th>
              <th className="py-2 pr-4 text-right">Durée</th>
            </tr>
          </thead>
          <tbody>
            {loading && logs.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-6 text-center text-zinc-500">
                  Chargement...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-6 text-center text-zinc-500">
                  Aucun log MCP trouvé
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr className="border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                    <td className="py-2 pr-2">
                      <button
                        onClick={() => toggleRow(log.id)}
                        className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
                      >
                        {expandedRows.has(log.id) ? '▼' : '▶'}
                      </button>
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap text-zinc-500">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="py-2 pr-4">
                      <div>{log.agent_name || '-'}</div>
                      {log.agent_id && (
                        <div className="text-xs text-zinc-400">ID: {log.agent_id}</div>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <code className="text-xs bg-zinc-100 dark:bg-zinc-800 px-1 py-0.5 rounded">
                        {log.tool_name}
                      </code>
                    </td>
                    <td className="py-2 pr-4">
                      <div>{log.server_name || '-'}</div>
                      {log.connection_name && (
                        <div className="text-xs text-zinc-400">{log.connection_name}</div>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-center">
                      {log.success ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          ✓ Succès
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                          ✗ Erreur
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <code className="text-xs">{formatDuration(log.duration_ms)}</code>
                    </td>
                  </tr>
                  {expandedRows.has(log.id) && (
                    <tr className="bg-zinc-50 dark:bg-zinc-900/50">
                      <td colSpan={7} className="py-3 px-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs font-semibold mb-1 text-zinc-600 dark:text-zinc-400">
                              Arguments
                            </div>
                            <pre className="text-xs bg-white dark:bg-zinc-800 p-2 rounded border overflow-auto max-h-48">
                              {JSON.stringify(log.tool_arguments, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <div className="text-xs font-semibold mb-1 text-zinc-600 dark:text-zinc-400">
                              {log.success ? 'Résultat' : 'Erreur'}
                            </div>
                            <pre className="text-xs bg-white dark:bg-zinc-800 p-2 rounded border overflow-auto max-h-48">
                              {log.success
                                ? log.result_text || '(vide)'
                                : log.error_message || '(vide)'}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-500">
                {total} résultat{total > 1 ? 's' : ''} • Page {page} sur {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 rounded border hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 rounded border hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
